import { useCallback } from "react";
import { useQueryClient } from "react-query";
import type { NearMeConfiguration } from "@/react/api/crmFiltersApi";
import type {
  LocationData,
  MapBoxFilterParams,
} from "@/react/components/shared/Filter/FilterTypes/MapboxInputFilter";
import { FilterButtonV2 } from "@/react/components/shared/FilterV3/FilterButtonV2";
import { useMemberFilterDesign } from "@/react/components/shared/FilterV3/MemberFilterDesignContext";
import { Icon } from "@/react/components/shared/Icon";
import type { BaseFilter } from "@/react/components/shared/MemberFilters/types";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { useUpdateLocationModal } from "./UpdateLocationModal";

interface NearMeProps {
  configuration: NearMeConfiguration;
  onApply: (value: BaseFilter | null) => void;
  param: MapBoxFilterParams;
}

export const NearMe = ({ onApply, configuration, param }: NearMeProps) => {
  const hasValue = Boolean(param.value);
  const isMemberFilterDesignEnabled = useMemberFilterDesign();

  const updateLocationModal = useUpdateLocationModal();

  const queryClient = useQueryClient();

  const handleClick = useCallback(async () => {
    const toggleValue = () => {
      if (param.value) {
        return null;
      }

      return {
        id: configuration.payload.near_me_location_filter_values?.id,
        name: configuration.payload.near_me_location_filter_values?.name,
        source: "mapbox_v6",
      };
    };

    if (!configuration.payload.require_user_to_provide_more_precise_location) {
      onApply?.({
        ...param,
        value: toggleValue(),
      });
      return;
    }
    await updateLocationModal.show({
      payload: configuration.payload,
      onSuccess: (data: { location: LocationData }) => {
        onApply?.({
          ...param,
          key: "near_me",
          value: data.location,
        });
        void updateLocationModal.hide();
        void queryClient.invalidateQueries(["crmFilters"]);
      },
    });
  }, [configuration.payload, updateLocationModal, param, onApply, queryClient]);

  const handleClear = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      onApply?.({
        ...param,
        value: null,
      });
    },
    [onApply, param],
  );

  if (!configuration.enabled) {
    return null;
  }

  if (isMemberFilterDesignEnabled) {
    return (
      <FilterButtonV2
        hasValue={hasValue}
        onClear={handleClear}
        onClick={handleClick}
        className="py-1.5 pl-[10px] pr-3"
      >
        <Icon
          type="16-location-filled"
          size={16}
          className="text-dark"
          useWithFillCurrentColor
        />
        <span className="text-dark text-sm font-medium leading-5">
          {configuration.label}
        </span>
      </FilterButtonV2>
    );
  }

  return (
    <div className="flex items-center gap-x-2">
      <button
        className={classNames(
          "hover:border-secondary focus-visible:border-secondary disabled:hover:border-primary text-default border-primary flex h-6 items-center gap-x-1 rounded-3xl border pl-1 pr-3 text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-50",
          { "text-dark border-secondary bg-secondary": hasValue },
        )}
        type="button"
        onClick={handleClick}
      >
        <Icon
          type="16-location-filled"
          size={16}
          className="text-dark"
          useWithFillCurrentColor
        />
        {configuration.label}
      </button>
      <div className="border-primary h-5 border-l" />
    </div>
  );
};
